import { Box, Image, Loader, Search } from '@wix/design-system';
import classNames from 'classnames';
import lodash from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';

import { PluginsEventsContext, RicosContext, useDevGiphyRequestService } from './../../ricos-context';
import { GIFData_GIFType } from './../../ricos-schema/next-schema';
import { useBiNext } from './../../_shared/bi-context-provider/bi-context-provider';
import type { PmGifNode } from './../../_shared/pm-node-types';
import { uuid } from './../../wix-rich-content-common/index';
import {
  imagePlaceholder,
  PAGE_SIZE,
  RICOS_DEFAULTS,
  WAIT_INTERVAL,
} from './../constants';

import {
  INSERT_GIF_MODAL_GIF_ITEM,
  INSERT_GIF_MODAL_GIF_SEARCH_INPUT,
  INSERT_GIF_MODAL_GIF_SELECTOR,
  INSERT_GIF_MODAL_STICKER_ITEM,
  INSERT_GIF_MODAL_STICKER_SEARCH_INPUT,
} from '../data-hooks';
import { PoweredByGiphy } from '../icons';
import type { GifsResult, GiphyPluginEditorConfig, IGif } from '../types';
import { GIPHY_TYPE } from '../types';
import { getGiphyProxyUrl } from './get-giphy-proxy-url';
import { GiphyEmptyState } from './giphy-empty-state';
import { GiphyErrorState } from './giphy-error-state';
import styles from './giphy-selector-scss';

export type ConvertedGiphyToComponentData = {
  originalUrl: string;
  originalMp4?: string;
  stillUrl: string;
  downsizedUrl: string;
  downsizedStillUrl: string;
  downsizedSmallMp4: string;
  height: number | string;
  width: number | string;
};

const convertGiphyToPmNode = (giphy: IGif): Omit<PmGifNode['attrs'], 'id'> => {
  return {
    containerData: { ...RICOS_DEFAULTS.containerData },
    original: {
      gif: giphy.images.original.url,
      mp4: giphy.images.original.mp4,
      still: giphy.images.original_still.url,
    },
    downsized: {
      gif: giphy.images.downsized?.url,
      mp4: giphy.images.downsized_small?.mp4,
      still: giphy.images.downsized_still.url,
    },
    height: Number(giphy.images.original.height),
    width: Number(giphy.images.original.width),
    gifType: giphy.is_sticker ? GIFData_GIFType.STICKER : GIFData_GIFType.NORMAL,
  };
};

type Props = {
  onGifAdd: (gif: Omit<PmGifNode['attrs'], 'id'>) => void;
  onCloseRequested: () => void;
  getGiphyUrl: GiphyPluginEditorConfig['getGiphyUrl'];
  category: 'gifs' | 'stickers';
  modalRef: HTMLDivElement | null;
  modalId: string;
};

type GifColumn = { gifs: IGif[]; totalHeight: number };

type State =
  | { type: 'LOADING'; searchTag: string; requestId: string }
  | {
      type: 'READY';
      columns: [GifColumn, GifColumn];
      hasMoreItems: boolean;
      page: number;
      totalLength: number;
      searchTag: string;
    }
  | {
      type: 'ERROR';
      searchTag: string;
    };

export const GiphySelector: React.FC<Props> = ({
  onGifAdd,
  onCloseRequested,
  getGiphyUrl,
  category,
  modalRef,
  modalId,
}) => {
  const [modalWidth, setModalWidth] = useState(0);
  const { t, isMobile } = useContext(RicosContext);
  const devGiphyRequestService = useDevGiphyRequestService();
  const [state, setState] = useState<State>({
    type: 'LOADING',
    searchTag: '',
    requestId: uuid(),
  });
  const logBi = useBiNext({ modalId, pluginId: 'giphy' });

  const pluginsEvents = useContext(PluginsEventsContext);

  const triggerBi = lodash.debounce(() => {
    pluginsEvents.publishPluginPopoverSearch({ pluginId: GIPHY_TYPE, searchTerm: state.searchTag });
  });

  useEffect(() => {
    if (modalRef) {
      const { width } = modalRef.getBoundingClientRect();
      setModalWidth(width);
    }
  }, [modalRef]);

  useEffect(() => {
    searchGifs('');
  }, []);

  const searchGifs = (searchTag: string) => {
    triggerBi();
    const requestId = uuid();
    setState({
      type: 'LOADING',
      searchTag,
      requestId,
    });

    getGifs(searchTag, 0, requestId);
  };

  const fetchTrendingGifs = async (page: number) => {
    if (devGiphyRequestService) {
      const { results } = await devGiphyRequestService.getTrendingGifs({
        path: 'trending',
        category,
        limit: 100,
        offset: page * 100,
      });
      return results;
    }

    const url = getGiphyProxyUrl(getGiphyUrl, {
      path: 'trending',
      category,
      limit: 100,
      offset: page * 100,
    });

    if (!url) {
      return setState({
        type: 'ERROR',
        searchTag: '',
      });
    }

    const result = await fetch(url)
      .then(async r =>
        r.status === 200
          ? { type: 'OK' as const, data: (await r.json()) as GifsResult }
          : Promise.reject()
      )
      .catch(() => ({ type: 'ERROR' as const }));

    if (result.type === 'ERROR') {
      return setState({
        type: 'ERROR',
        searchTag: '',
      });
    }

    return result.data;
  };

  const fetchSearchGifs = async (searchTag: string, page: number) => {
    if (devGiphyRequestService) {
      const { results } = await devGiphyRequestService.getSearchGifs({
        path: 'search',
        category,
        limit: PAGE_SIZE + 1,
        offset: page * PAGE_SIZE,
        searchTag,
      });
      return results;
    }

    const url = getGiphyProxyUrl(getGiphyUrl, {
      path: 'search',
      category,
      limit: PAGE_SIZE + 1,
      offset: page * PAGE_SIZE,
      searchTag,
    });

    if (!url) {
      return setState({
        type: 'ERROR',
        searchTag: '',
      });
    }

    const result = await fetch(url)
      .then(async r =>
        r.status === 200
          ? { type: 'OK' as const, data: (await r.json()) as GifsResult }
          : Promise.reject()
      )
      .catch(() => ({ type: 'ERROR' as const }));

    if (result.type === 'ERROR') {
      return setState({
        type: 'ERROR',
        searchTag: '',
      });
    }

    return result.data;
  };

  const processNewGifs = (gifsInfo: GifsResult, page: number, requestId: string) => {
    const newGifs = gifsInfo.data;
    const currentTabOrder = state.type === 'READY' && page !== 0 ? state.totalLength : 0;

    const updatedColumns: [GifColumn, GifColumn] = newGifs.reduce(
      (columns, gif, index) => {
        const { height } = gif.images.fixed_width_downsampled;

        const columnIndex = columns[0].totalHeight <= columns[1].totalHeight ? 0 : 1;
        columns[columnIndex].gifs.push({
          ...gif,
          tabOrder: currentTabOrder + index,
        });
        columns[columnIndex].totalHeight += Number(height);

        return columns;
      },
      page === 0
        ? [
            { gifs: [], totalHeight: 0 },
            { gifs: [], totalHeight: 0 },
          ]
        : [...(state as { columns: [GifColumn, GifColumn] }).columns]
    );

    setState(prev => {
      if (prev.type === 'LOADING' && prev.requestId !== requestId) {
        return prev;
      }

      return {
        type: 'READY',
        columns: updatedColumns,
        hasMoreItems: newGifs.length < gifsInfo.pagination.total_count,
        page: page + 1,
        totalLength: updatedColumns[0].gifs.length + updatedColumns[1].gifs.length,
        searchTag: prev.searchTag,
      };
    });
  };

  const getGifs = async (searchTag: string, page: number, requestId: string) => {
    const data = searchTag ? await fetchSearchGifs(searchTag, page) : await fetchTrendingGifs(page);
    if (data) {
      processNewGifs(data, page, requestId);
    }
  };

  const selectGif = (gif: Omit<PmGifNode['attrs'], 'id'>) => {
    logBi({
      dataHook: category === 'gifs' ? INSERT_GIF_MODAL_GIF_ITEM : INSERT_GIF_MODAL_STICKER_ITEM,
    });
    onGifAdd(gif);
    onCloseRequested();
  };

  const onClick = (giphy: IGif) => {
    const componentData = convertGiphyToPmNode(giphy);
    selectGif(componentData);
  };

  const handleNavigation = (tabOrder: number, offset: 1 | -1) => {
    if (!modalRef?.parentElement) {
      return;
    }

    const gifs = state.type === 'READY' ? state.columns.flatMap(col => col.gifs) : [];

    const targetGif = gifs.find(gif => gif.tabOrder === tabOrder + offset);

    if (targetGif) {
      const gifElement: HTMLElement | null = modalRef.parentElement.querySelector(
        `[data-id="${targetGif.id}${targetGif.tabOrder}"]`
      );

      if (gifElement) {
        gifElement.focus();
      }
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLImageElement>, giphy: IGif) => {
    if ((e.key === 'Tab' && !e.shiftKey) || e.key === 'ArrowRight') {
      e.preventDefault();
      handleNavigation(giphy.tabOrder || 0, 1);
    }

    if ((e.shiftKey && e.key === 'Tab') || e.key === 'ArrowLeft') {
      if (giphy.tabOrder === 0) {
        return;
      }
      e.preventDefault();
      handleNavigation(giphy.tabOrder || 0, -1);
    }

    if (e.key === 'Escape') {
      onCloseRequested?.();
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const componentData = convertGiphyToPmNode(giphy);
      selectGif(componentData);
    }
  };

  return (
    <Box direction="vertical" padding={'SP3'} height={'430px'}>
      <Search
        placeholder={
          category === 'gifs'
            ? t('GiphyUploadModal_Search_Placeholder')
            : t('GiphyUploadModal_Search_Placeholder_Stickers')
        }
        onChange={e => {
          logBi({
            dataHook:
              category === 'gifs'
                ? INSERT_GIF_MODAL_GIF_SEARCH_INPUT
                : INSERT_GIF_MODAL_STICKER_SEARCH_INPUT,
            value: e.target.value,
          });
          searchGifs(e.target.value);
        }}
        debounceMs={WAIT_INTERVAL}
        onClear={() => searchGifs('')}
        value={state.searchTag}
        dataHook={
          category === 'gifs'
            ? INSERT_GIF_MODAL_GIF_SEARCH_INPUT
            : INSERT_GIF_MODAL_STICKER_SEARCH_INPUT
        }
        className={styles.searchInput}
      />
      {state.type === 'ERROR' ? (
        <div data-hook={INSERT_GIF_MODAL_GIF_SELECTOR}>
          <GiphyErrorState />
        </div>
      ) : (
        <div data-hook={INSERT_GIF_MODAL_GIF_SELECTOR}>
          <div
            className={classNames(styles.giphy_selector_infinite_scroll_container, {
              [styles.mobile]: isMobile,
            })}
          >
            {state.type === 'READY' && !state.columns[0].gifs.length && state.searchTag ? (
              <GiphyEmptyState category={category} />
            ) : state.type === 'LOADING' ? (
              <Box width="100%" height="100%" align="center" verticalAlign="middle">
                <Loader size="small" />
              </Box>
            ) : state.type === 'READY' ? (
              <InfiniteScroll
                pageStart={0}
                loadMore={async () => getGifs(state.searchTag, state.page, uuid())}
                hasMore={state.hasMoreItems}
                loader={
                  <div className={styles.giphy_selector_spinner_load_more}>
                    <Loader size="small" />
                  </div>
                }
                useWindow={false}
                className={
                  state.columns[0].gifs.length > 0
                    ? styles.giphy_selector_infinite_scroll
                    : styles.giphy_selector_spinner_empty_modal
                }
              >
                <Box gap="18px" display="flex" verticalAlign="space-between">
                  {state.columns.map((column, columnIndex) => (
                    <Box key={columnIndex} display="flex" direction="vertical" gap={'18px'}>
                      {column.gifs.map(giphy => (
                        <Image
                          key={giphy.id.toString()}
                          data-id={`${giphy.id.toString()}${giphy.tabOrder}`}
                          tabIndex={0}
                          transparent
                          onKeyDown={e => onKeyDown(e, giphy)}
                          onClick={() => onClick(giphy)}
                          dataHook={
                            category === 'gifs'
                              ? INSERT_GIF_MODAL_GIF_ITEM
                              : INSERT_GIF_MODAL_STICKER_ITEM
                          }
                          id={giphy.id.toString()}
                          role="img"
                          aria-label={category}
                          width={giphy.images.fixed_width_downsampled.width}
                          height={calculateHeight(giphy, modalWidth)}
                          fit="fill"
                          src={
                            giphy.images.fixed_width_downsampled.webp ||
                            giphy.images.fixed_width_downsampled.url ||
                            imagePlaceholder
                          }
                          className={styles.giphy_selector_gif_img_container}
                          showBorder
                          borderRadius="8px"
                          alt={giphy.alt_text || category}
                        />
                      ))}
                    </Box>
                  ))}
                </Box>
              </InfiniteScroll>
            ) : null}
          </div>
          <Box display="block" width="100%" paddingTop="8px">
            <PoweredByGiphy className={styles.giphy_selector_powerdByGiphy} />
          </Box>
        </div>
      )}
    </Box>
  );
};

const calculateHeight = (giphy: IGif, modalWidth: number) => {
  const originalHeight = Number(giphy.images.fixed_width_downsampled.height);
  const originalWidth = Number(giphy.images.fixed_width_downsampled.width);
  const paddings = 18 * 3; // modal padding + gap between columns
  const newWidth = (modalWidth - paddings) / 2;

  return (originalHeight / originalWidth) * newWidth;
};
